'use client';

import { useRef, useState, useCallback } from 'react';
import BlurImage from '@/components/BlurImage';
import Link from 'next/link';
import siteTheme from '@/lib/theme';
import type { HomeSectionLocation } from '@/lib/cms';
import ArrowIcon from '@/components/ArrowIcon';
const { colors, fonts } = siteTheme;

const pad     = 'clamp(2rem, 6vw, 8rem)';
const section = 'clamp(7rem, 11vw, 13rem)';

export default function LocationSection({ data }: { data: HomeSectionLocation }) {
  const IMAGES = data.images.map((img, i) => ({
    url:   img.url,
    label: data.image_labels?.[i] ?? img.alternativeText ?? `Image ${i + 1}`,
  }));

  const firstLetter = data.body.charAt(0);
  const restBody    = data.body.slice(1);

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.offsetWidth / 2;
    const index = Math.min(IMAGES.length - 1, Math.round(el.scrollLeft / cardWidth));
    setActiveIndex(index);
  }, [IMAGES.length]);

  const scrollTo = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * (el.offsetWidth / 2), behavior: 'smooth' });
  };

  const prev = () => scrollTo(Math.max(0, activeIndex - 1));
  const next = () => scrollTo(Math.min(IMAGES.length - 1, activeIndex + 1));

  return (
    <section style={{ backgroundColor: colors.bgAlt }}>

      {/* ══════════════════════════════════════════════════════
          DESKTOP
      ══════════════════════════════════════════════════════ */}
      <div className="hidden lg:block" style={{ padding: `${section} ${pad}` }}>
        <div style={{ maxWidth: '144rem', margin: '0 auto' }}>

          {/* ── Row 1: Centered header — label + heading + script ── */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(5rem, 7vw, 8rem)' }}>
            <p className="section-label" style={{ letterSpacing: '0.28em', marginBottom: '1.4rem' }}>
              {data.label}
            </p>
            <h2 className="section-title" style={{ color: colors.navyDeep, letterSpacing: '0.03em', marginBottom: '1rem' }}>
              {data.heading}
            </h2>
            <p className="section-script" style={{ color: colors.goldLight }}>
              {data.script_location}
            </p>
          </div>

          {/* ── Row 2: 2-col — body+button LEFT | 3 images RIGHT ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 'clamp(4rem, 6vw, 9rem)',
            alignItems: 'start',
          }}>
            {/* LEFT: body text + button */}
            <div>
              <p className="section-body" style={{ fontSize: 'clamp(1.6rem, 1.7vw, 2rem)', lineHeight: 1.85, color: colors.grey, marginBottom: '3.5rem' }}>
                <span style={{ fontFamily: fonts.script, fontSize: '2.4em', lineHeight: 0.45, float: 'left', marginRight: '0.15em', marginTop: '0.05em', color: colors.gold }}>{firstLetter}</span>
                {restBody}
              </p>
              {data.button_href && data.button_label && (
                <Link href={data.button_href} className="btn-luxury-gold" style={{
                  fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
                  letterSpacing: '0.06em', color: colors.gold,
                  border: `1px solid ${colors.goldPale}`,
                  borderRadius: '0.5rem',
                  padding: '1.1rem 2.4rem',
                  display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
                  textDecoration: 'none',
                }}>
                  {data.button_label} <ArrowIcon variant="short" />
                </Link>
              )}
            </div>

            {/* RIGHT: 3 images with labels */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'clamp(1.2rem, 2vw, 2.4rem)',
            }}>
              {IMAGES.map(({ url, label }) => (
                <div key={url} className="group cursor-default">
                  <div style={{ position: 'relative', paddingBottom: '100%', overflow: 'hidden', borderRadius:'0.5rem' }}>
                    <BlurImage
                      src={url} alt={label} fill
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      sizes="22vw"
                    />
                  </div>
                  <p className="card-meta" style={{ textAlign: 'center', padding: '1.4rem 0 0' }}>
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          MOBILE
      ══════════════════════════════════════════════════════ */}
      <div className="lg:hidden" style={{ padding: 'clamp(5rem, 8vw, 7rem) clamp(2rem, 5vw, 4rem) 0' }}>

        <p className="section-label" style={{ marginBottom: '1.2rem', textAlign: 'center' }}>
          {data.label}
        </p>

        <h2 className="section-title" style={{ fontSize: 'clamp(2.6rem, 7vw, 3.6rem)', color: colors.navyDeep, letterSpacing: '0.03em', textAlign: 'center', marginBottom: '0.8rem' }}>
          {data.heading}
        </h2>

        <p className="section-script" style={{ fontSize: 'clamp(3rem, 7vw, 4rem)', color: colors.goldLight, textAlign: 'center', marginBottom: 'clamp(3rem, 6vw, 4rem)' }}>
          {data.script_location}
        </p>

        <p className="section-body" style={{ fontSize: 'clamp(1.5rem, 3.8vw, 1.8rem)', lineHeight: 1.8, color: colors.grey, marginBottom: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
          <span style={{ fontFamily: fonts.script, fontSize: '2.2em', lineHeight: 0.8, verticalAlign: 'baseline' , color: colors.gold }}>{firstLetter}</span>
          {restBody}
        </p>

        {data.button_href && data.button_label && (
          <div style={{ marginBottom: 'clamp(3.5rem, 7vw, 5rem)' }}>
            <Link href={data.button_href} className="btn-luxury-gold" style={{
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
              letterSpacing: '0.05em', color: colors.gold,
              border: `1px solid ${colors.goldPale}`,
              borderRadius: '0.5rem',
              padding: '1.2rem 2.6rem',
              display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
              textDecoration: 'none',
            }}>
              {data.button_label} <ArrowIcon variant="short" />
            </Link>
          </div>
        )}

        {/* Image carousel — 2 visible at a time */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            display: 'flex',
            overflowX: 'scroll',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            gap: '1rem',
            marginLeft: '-clamp(2rem, 5vw, 4rem)',
            marginRight: '-clamp(2rem, 5vw, 4rem)',
            paddingLeft: 'clamp(2rem, 5vw, 4rem)',
            paddingRight: 'clamp(2rem, 5vw, 4rem)',
          }}
        >
          {IMAGES.map(({ url, label }, i) => (
            <div
              key={url}
              className="group"
              style={{
                flex: '0 0 calc(50% - 0.5rem)',
                minWidth: 'calc(50% - 0.5rem)',
                scrollSnapAlign: 'start',
              }}
            >
              <div style={{ position: 'relative', paddingBottom: '120%', overflow: 'hidden' }}>
                <BlurImage
                  src={url} alt={label} fill
                  className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  sizes="50vw"
                  priority={i === 0}
                />
              </div>
              <p style={{
                fontFamily: fonts.sans, fontWeight: 400,
                fontSize: '1.4rem', letterSpacing: '0.2em', textTransform: 'uppercase',
                color: colors.goldLight, textAlign: 'center',
                padding: '1.4rem 0 0',
              }}>
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Navigation: ← N/3 → */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '2.4rem', padding: 'clamp(2.5rem, 5vw, 3.5rem) 0',
        }}>
          <button onClick={prev} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontFamily: fonts.sans, fontSize: '1.8rem',
            color: activeIndex === 0 ? 'rgba(0,0,0,0.2)' : colors.goldLight,
            lineHeight: 1, transition: 'color 0.2s', display: 'flex', alignItems: 'center',
          }}><ArrowIcon direction="left" variant="short" size={16} /></button>
          <p style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
            letterSpacing: '0.15em', color: colors.goldLight,
          }}>
            {activeIndex + 1} / {IMAGES.length}
          </p>
          <button onClick={next} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: activeIndex === IMAGES.length - 1 ? 'rgba(0,0,0,0.2)' : colors.goldLight,
            lineHeight: 1, transition: 'color 0.2s', display: 'flex', alignItems: 'center',
          }}><ArrowIcon variant="short" size={16} /></button>
        </div>

      </div>
    </section>
  );
}
